import * as React from "react";
import styled from "@mui/material/styles/styled";
import { ReactComponent as BotIcon } from "../../assets/botAvatar.svg";
import { GenderValue } from "shared";
import { AVATAR_HEIGHT, CHAT_ELEMENT_HEIGHT } from "../consts/sizeConsts";
import { PatientColorMap } from "../../layout/patientColorMap";
import { useNavigation } from "../../state/navigationContext";
import GenderIcon from "fe-web-components/genderIcon/GenderIcon";
import {
  marginLeftToMarginRightOnRtl,
  onRtl,
  paddingLeftToPaddingRightOnRtl,
} from "fe-web-components/rtl/rtlStyles";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { useTranslate } from "../../hooks/useTranslate";

//region [[ Styles ]]
const AnsewerContainer = styled("div")({
  display: "flex",
  flexFlow: "row-reverse",
  flexWrap: "nowrap",
  flex: 1,
});

const TextAnswerView = styled("div")(({ theme }) => ({
  borderRadius: "30px",
  display: "flex",
  minHeight: CHAT_ELEMENT_HEIGHT,
  alignItems: "center",
  backgroundColor: PatientColorMap.Primary,
  minWidth: "90px",
  color: PatientColorMap.White,
  "&.numeric.ltr .ansewer-text, & .ansewer-text > span.numeric.ltr": {
    direction: "ltr",
    // override the padding right to padding left on rtl
    paddingRight: "0",
    paddingLeft: "15px",
  },
  ...paddingLeftToPaddingRightOnRtl(15),
  "&.long-format": {
    // Win specificity over rtl -^
    ...onRtl({ padding: "0 20px" }),
    padding: "0 20px",
    flex: 1,
    width: "100%",
    background: PatientColorMap.Primary,
    border: `1px solid ${PatientColorMap.Primary}`,
    color: PatientColorMap.White,
    "& .bold": {
      fontWeight: 700,
    },
  },
}));

const BotIconStyled = styled(BotIcon)({
  height: AVATAR_HEIGHT,
  width: AVATAR_HEIGHT,
  ...marginLeftToMarginRightOnRtl(5),
});

const ShowMoreButton = styled("span")({
  color: PatientColorMap.PrimaryDark,
  cursor: "pointer",
  fontWeight: 400,
  width: "100%",
});

const TextAnswerText = styled("div")({
  fontSize: "16px",
  color: PatientColorMap.AnswerTextColor,
  padding: "12px 0",
  ".long-format > &": {
    color: PatientColorMap.White,
  },
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface TextAnswerProps {
  className?: string;
  answerText: React.ReactNode;
  gender?: GenderValue;
  onClick?: () => void;
  isNumeric?: boolean;
  keepLtr?: boolean;
  longFormat?: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]
const MAX_STRING_CHARS = 300;
export const TextAnswer = ({ ...props }: TextAnswerProps) => {
  const { themeSettings } = useNavigation();
  const [textIsLimited, setTextIsLimited] = useState(true);
  const textRef = useRef<HTMLDivElement>();

  const textNeedsToBeLimited =
    typeof props.answerText === "string"
      ? props.answerText.length > MAX_STRING_CHARS
      : false;

  const renderUserIcon = () => {
    if (props.gender) {
      return (
        <GenderIcon small gender={props.gender} themeSettings={themeSettings} />
      );
    }
    return <BotIconStyled fill="red" stroke="green" />;
  };

  const { translate } = useTranslate();

  useEffect(() => {
    if (textNeedsToBeLimited) {
      textRef.current?.scrollIntoView?.({ block: "start", behavior: "smooth" });
    }
  }, [textNeedsToBeLimited, textIsLimited]);

  let answerText =
    typeof props.answerText === "string"
      ? (props.answerText as string).replace(/<[^>]*>?/gm, "")
      : Array.isArray(props.answerText)
      ? props.answerText.join(" ").replace(/<[^>]*>?/gm, "")
      : props.answerText;

  if (
    typeof props.answerText === "string" &&
    (answerText as string).trim() === ""
  )
    answerText = translate("NO_INFO");

  return (
    <AnsewerContainer
      className={clsx(
        "answer-container",
        props.longFormat ? "long-format" : "",
      )}
    >
      {props.longFormat && renderUserIcon()}
      <TextAnswerView
        className={clsx(
          "text-answer-view",
          props.className,
          props.isNumeric ? "numeric" : "",
          props.keepLtr ? "ltr" : "",
          props.longFormat ? "long-format" : "",
        )}
        onClick={props.onClick}
      >
        <TextAnswerText className={"ansewer-text"} ref={textRef}>
          {textNeedsToBeLimited && textIsLimited && (
            <>
              {(answerText as string).slice(0, MAX_STRING_CHARS)}
              ...
              <ShowMoreButton onClick={() => setTextIsLimited(false)}>
                {translate("SHOW_MORE")}
              </ShowMoreButton>
            </>
          )}
          {textNeedsToBeLimited && !textIsLimited && answerText}
          {textNeedsToBeLimited && !textIsLimited && (
            <ShowMoreButton onClick={() => setTextIsLimited(true)}>
              {translate("SHOW_LESS")}
            </ShowMoreButton>
          )}
          {!textNeedsToBeLimited && (
            <span dangerouslySetInnerHTML={{ __html: answerText as string }} />
          )}
        </TextAnswerText>
        {!props.longFormat && renderUserIcon()}
      </TextAnswerView>
    </AnsewerContainer>
  );
};
